import { useCallback, useEffect, useMemo } from 'react';
// hooks
import useCheckout from 'src/hooks/use-checkout'
// redux
import { useDispatch, useSelector } from 'src/redux/store';
import {
  resetCustomers,
  updateCustomers,
  updateCustomer,
  fetchOneCustomer,
  fetchCustomersData,
} from 'src/redux/slice/customer';
import { selectCustomer } from 'src/redux/slice/checkout'
// auth
import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

export default function useCustomers() {
  const dispatch = useDispatch();
  const { user } = useAuthContext()
  const { customerSelected } = useCheckout()

  const { allData: customers, isLoading, error, nextPage } = useSelector((state) => state.customers)

  const config = user?.configSelected?.id

  // Auto-initialize customers on mount if not already loaded
  useEffect(() => {
    if (!customers[0] && !isLoading && user?.id) {
      const fetchCustomers = (page) => {
        dispatch(fetchCustomersData({ idUser: user?.id, config, page }))
          .unwrap()
          .then((res) => {
            if (res.nextPage) fetchCustomers(res.nextPage);
          });
      };
      fetchCustomers(nextPage || 1);
    }
  }, [customers, isLoading, user?.id, config, nextPage, dispatch]);

  const onRefreshCustomers = useCallback(
    () => {
      const fetchCustomers = (page) => {
        dispatch(fetchCustomersData({ idUser: user?.id, config, page }))
          .unwrap()
          .then((res) => {
            if (res.nextPage) fetchCustomers(res.nextPage);
          });
      };
      fetchCustomers(nextPage || 1);
    },
    [dispatch, user?.id, nextPage, config]
  );

  const onRefreshOneCustomer = useCallback(
    (id) => dispatch(fetchOneCustomer({ idUser: user?.id, config, id })).unwrap(),
    [dispatch, user?.id, config]
  );

  const onInitCustomers = useCallback(
    () => {
      if (!customers[0]) {
        onRefreshCustomers();
      }
    },
    [customers, onRefreshCustomers],
  );

  const onResetCustomers = useCallback(
    () => {
      dispatch(resetCustomers());
    },
    [dispatch],
  );

  const onUpdateCustomers = useCallback(
    (data) => {
      dispatch(updateCustomers({ data }))
    },
    [dispatch]
  );

  const onUpdateCustomer = useCallback(
    (customer) => {
      if (customerSelected.email === customer.email) dispatch(selectCustomer(customer));
      dispatch(updateCustomer(customer))
    },
    [dispatch, customerSelected]
  );

  // Memoized return value
  return useMemo(
    () => ({
      allCustomers: customers,
      isLoading,
      error,
      onRefreshCustomers,
      onInitCustomers,
      onResetCustomers,
      onRefreshOneCustomer,
      onUpdateCustomers,
      onUpdateCustomer,
    }),
    [
      customers,
      isLoading,
      error,
      onRefreshCustomers,
      onInitCustomers,
      onResetCustomers,
      onRefreshOneCustomer,
      onUpdateCustomers,
      onUpdateCustomer,
    ]
  );
}
